// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getDataViewKey } from "@insite/client-framework/Store/Data/DataState";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Clickable from "@insite/mobius/Clickable";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkProps } from "@insite/mobius/Link";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SectionTitle from "@werner/App/Components/Common/SectionTitle";
import XngCarousel, { SwiperStyle } from "@werner/App/Components/XngCarousel";
import loadShopbyCategories from "@werner/App/Handlers/ShopByCategory/loadShopByCategories";
import { CustomAppState } from "@werner/App/Store/Reducers";
import { getSettingsCollection } from "@werner/App/Store/Selectors/SettingsSelectors";
import { getShopbyCategories } from "@werner/App/Store/Selectors/ShopByCategoriesSelector";
import { SLIDES_PER_VIEW_POINTS } from "@werner/XDL/constants";
import { color, generateHoverStyleForLink, linkArrowStyle, mergeWithCss, xScreen } from "@werner/XDL/Utilities";
import { generateSlidesPerViewMap } from "@werner/XDL/Utilities/helpers";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    viewAllText = "viewAllText",
    viewAllLink = "viewAllLink",
    categoriesToDisplay = "categoriesToDisplay",
    numberOfCategoriesToDisplay = "numberOfCategoriesToDisplay",
    categoriesSelected = "categoriesSelected",
    showTopBorder = "showTopBorder",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.viewAllText]: string;
        [fields.viewAllLink]: LinkFieldValue;
        [fields.categoriesToDisplay]: "featured" | "select-categories";
        [fields.numberOfCategoriesToDisplay]: string;
        [fields.categoriesSelected]: string[];
        [fields.showTopBorder]: boolean;
    };
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const { categoriesSelected, categoriesToDisplay } = ownProps.fields;
    const isFeatured = categoriesToDisplay === "featured";
    const type = isFeatured ? "0" : "1";
    const categoriesIds = isFeatured ? "" : categoriesSelected.filter(item => item !== "").join(",");

    const key = getDataViewKey({
        displayShopByCategoryFromType: type,
        selectedCategoryIds: categoriesIds,
    });

    const categoriesData = getShopbyCategories(state, key);

    return {
        isLoading: categoriesData?.isLoading,
        categories: categoriesData?.data,
        smallDefaultCategoryImage: getSettingsCollection(state).customSettings.smallDefaultCategoryImage,
        type,
        categoriesIds,
    };
};

const mapDispatchToProps = {
    loadShopbyCategories,
};

export interface ShopByCategoryStyles {
    categoryWrapper?: InjectableCss;
    imageItem?: InjectableCss;
    imageClickable?: LinkProps;
    image?: LazyImageProps;
    skipToContent?: SkipNavStyles;
    categoryLink?: LinkProps;
    swiperStyle?: SwiperStyle;
}

export const shopByCategoryStyles: ShopByCategoryStyles = {
    categoryWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 12px;
            width: 166px;
            ${xScreen("lte", "xs")} {
                width: 100%;
            }
        `,
    },
    imageItem: {
        css: css`
            width: 166px;
            height: 166px;
            box-sizing: border-box;
            cursor: pointer;
            display: flex;
            justify-content: center;
            padding: 10px;
            box-shadow: 0px 0px 15px 0 ${color("primary", 0.15)};
            border: 1px solid ${color("primary", 0.05)};
            transition: box-shadow 0.15s linear;
            &:hover {
                box-shadow: 0px 0px 15px 5px ${color("primary", 0.15)};
            }
            ${xScreen("lte", "xs")} {
                width: 100%;
                height: auto;
            }
        `,
    },
    imageClickable: {
        css: css`
            width: 146px;
            height: 146px;
            justify-content: center;
        `,
    },
    image: {
        width: "146px",
        height: "146px",
        css: css`
            & img {
                max-width: 100%;
                max-height: 100%;
                height: auto;
            }
        `,
    },

    categoryLink: {
        ...mergeWithCss(linkArrowStyle, {
            color: "primary",
            icon: {
                iconProps: {
                    size: 10,
                    color: "primary",
                },
            },
            css: css`
                justify-content: center;
                ${generateHoverStyleForLink("common.ui3", "primary")}
                & ${TypographyStyle} {
                    max-width: 100%;
                    overflow: hidden;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                }
            `,
        }),
    },
    swiperStyle: {
        container: {
            css: css`
                .xng-pagination-container {
                    height: 50px;
                    margin-top: 0;
                }
                position: relative;
                &:before {
                    content: " ";
                    width: calc(100% - 30px);
                    height: 100%;
                    background: ${color("common.white")};
                    position: absolute;
                    left: -100%;
                    z-index: 2;
                }
            `,
        },
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = shopByCategoryStyles;

export const ShopByCategory: FC<Props> = ({
    fields,
    isLoading,
    categories,
    smallDefaultCategoryImage,
    type,
    categoriesIds,
    loadShopbyCategories,
}) => {
    const {
        title,
        viewAllLink,
        viewAllText,
        showTopBorder,
        categoriesToDisplay,
        numberOfCategoriesToDisplay,
        categoriesSelected,
    } = fields;

    useEffect(() => {
        loadShopbyCategories({
            displayShopByCategoryFromType: type,
            selectedCategoryIds: categoriesIds,
        });
    }, [categoriesSelected, categoriesToDisplay]);

    if (!categories?.categoriesList?.length) {
        return null;
    }

    const categoriesList = categories.categoriesList?.slice(0, parseInt(numberOfCategoriesToDisplay || "7", 10));

    const breakpoints = generateSlidesPerViewMap({
        [SLIDES_PER_VIEW_POINTS.xs]: 1,
        [SLIDES_PER_VIEW_POINTS.sm]: 3,
        [SLIDES_PER_VIEW_POINTS.md]: 4,
        [SLIDES_PER_VIEW_POINTS.lg]: 6,
    });

    const renderCategories = categoriesList?.map((category: any) => (
        <StyledWrapper {...styles.categoryWrapper} key={category.id}>
            <StyledWrapper {...styles.imageItem} data-test-selector="category-image">
                <Clickable
                    href={category.urlSegment}
                    {...styles.imageClickable}
                    aria-label={`${category.shortDescription} Category Image Clickable`}
                >
                    <LazyImage
                        {...styles.image}
                        src={category.smallImagePath || smallDefaultCategoryImage}
                        altText={`${translate("Go to Category page")} ${
                            category.imageAltText || category.shortDescription
                        }`}
                    />
                </Clickable>
            </StyledWrapper>
            <Link
                href={category.urlSegment}
                {...styles.categoryLink}
                aria-label={`${category.shortDescription} Category Link`}
            >
                {category.shortDescription}
            </Link>
        </StyledWrapper>
    ));

    return (
        <>
            <SectionTitle
                title={title}
                viewMoreLink={viewAllLink}
                viewMoreText={viewAllText}
                border={showTopBorder ? "top" : "none"}
            />
            <XngCarousel
                spaceBetween={30}
                breakpoints={breakpoints}
                scrollbar={{ draggable: true, el: ".swiper-scrollbar", dragSize: "auto" }}
                className="with-padding"
                extendedStyles={styles.swiperStyle}
            >
                {renderCategories}
            </XngCarousel>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ShopByCategory),
    definition: {
        displayName: "Shop By Category",
        icon: "list-ul",
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.viewAllText,
                displayName: "Categories Link Title",
                editorTemplate: "TextField",
                defaultValue: "View All",
                fieldType: "Translatable",
            },
            {
                name: fields.viewAllLink,
                displayName: "Categories Link Path",
                editorTemplate: "LinkField",
                defaultValue: {
                    type: "Page",
                    value: "",
                },
                fieldType: "Translatable",
            },
            {
                name: fields.categoriesToDisplay,
                displayName: "Categories to display",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Featured",
                        value: "featured",
                    },
                    {
                        displayName: "Select categories",
                        value: "select-categories",
                    },
                ],
                defaultValue: "featured",
                horizontal: true,
                fieldType: "General",
            },
            {
                name: fields.numberOfCategoriesToDisplay,
                displayName: "Number of categories to display",
                editorTemplate: "DropDownField",
                defaultValue: "7",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                    { displayName: "9", value: "9" },
                    { displayName: "10", value: "10" },
                    { displayName: "11", value: "11" },
                    { displayName: "12", value: "12" },
                    { displayName: "13", value: "13" },
                    { displayName: "14", value: "14" },
                    { displayName: "15", value: "15" },
                    { displayName: "16", value: "16" },
                    { displayName: "17", value: "17" },
                    { displayName: "18", value: "18" },
                    { displayName: "19", value: "19" },
                    { displayName: "20", value: "20" },
                    { displayName: "21", value: "21" },
                    { displayName: "22", value: "22" },
                    { displayName: "23", value: "23" },
                    { displayName: "24", value: "24" },
                    { displayName: "25", value: "25" },
                ],
                fieldType: "General",
            },
            {
                name: fields.categoriesSelected,
                displayName: "Select categories",
                editorTemplate: "CategoriesField",
                isVisible: item => item.fields.categoriesToDisplay === "select-categories",
                defaultValue: [],
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.showTopBorder,
                displayName: "Show Top Border ",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
